/* iceberg-ui */
import { Card } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, CircularProgress, Tab } from '@mui/material';

/* Shared  */
import { EmptyState } from 'application/Shared/Components/Dashboard/Panels/EmptyPanel';
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';

/*  Helpers */
import { Tenant } from 'application/Shared/types/tenant';
import { useFetchGlobalAdminClientLinks } from 'hooks/dataAccess/GlobalAdmin/ClientLink/clientLink';
import { useFetchGlobalAdminTenantById } from 'hooks/dataAccess/GlobalAdmin/Tenant/tenant';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Banners } from './Banners';
import { EditTenant } from './EditTenant';
import { TenantDetails } from './TenantDetails';
import { TenantHeader } from './TenantHeader';

const TenantTabs = ({ activeTab }: { activeTab: string }) => {
    const { t } = useTranslation();
    const { tenantId } = useParams();

    const tabs: Array<string> = ['details', 'edit'];

    return (
        <MUITabsWrapper value={activeTab}>
            {tabs.map((name: string) => (
                <Tab
                    key={name}
                    component={Link}
                    to={`/globaladmin/tenant/${tenantId}/${name}`}
                    data-trackid={`globalAdmin_tenant_${name}`}
                    label={t(`GlobalAdmin.Tenant.Tabs.${name}`)}
                    value={name}
                    aria-label={`global admin tenant ${name}`}
                />
            ))}
        </MUITabsWrapper>
    );
};

export const TenantPage = () => {
    const { t } = useTranslation();
    const { tenantId, tab } = useParams();
    const activeTab = tab ?? 'details';

    const { tenant, loading, refetchData } = useFetchGlobalAdminTenantById({
        enabled: !!tenantId,
        tenantId: tenantId!
    });

    const tenantTenantParms = useMemo(
        () => ({
            global: true,
            includeTenant: true,
            linkedTenantId: tenant?.id,
            region: tenant?.region
        }),
        [tenant?.id, tenant?.region]
    );

    // Only load the partner tenant if the main tenant is not a partner.
    // To load the partner tenant, we first need to find the client's corresponding clientLink,
    // and then use the platformTenantId to fetch the partner tenant.
    const { clientLinks, loading: loadingClientLinks } =
        useFetchGlobalAdminClientLinks({
            enabled: !!tenant && !!tenant?.id && !tenant?.partner,
            params: tenantTenantParms
        });

    // Find the relevant partner-associated clientLink once clientLinks have loaded
    const relevantClientLink = useMemo(() => {
        if (!loadingClientLinks && Array.isArray(clientLinks)) {
            return clientLinks.find((cl) => cl.linkedTenantId === tenant?.id);
        }
        return undefined;
    }, [loadingClientLinks, clientLinks, tenant?.id]);

    // Contiditionally load the partner tenant if the relevant clientLink is found.
    const { tenant: partnerTenant } = useFetchGlobalAdminTenantById({
        enabled: !!relevantClientLink?.platformTenantId,
        tenantId: relevantClientLink?.platformTenantId
    });

    if (loading) {
        return <CircularProgress />;
    }

    if (!loading && !tenant) {
        return (
            <Card sx={{ margin: '24px' }}>
                <EmptyState
                    actions={[
                        {
                            href: `/globaladmin/tenant/`,
                            label: t('GlobalAdmin.Tenant.backToTenants')
                        }
                    ]}
                    content="GlobalAdmin.Tenant.notFound"
                    image="warning"
                />
            </Card>
        );
    }

    return (
        <>
            <TenantHeader
                tenant={tenant as Tenant}
                partnerTenant={partnerTenant as Tenant}
            />

            <Banners tenant={tenant} partnerTenant={partnerTenant} />

            <Box maxWidth={1280} mx="auto">
                <TenantTabs activeTab={activeTab} />

                {activeTab === 'details' && (
                    <TenantDetails
                        tenant={tenant as Tenant}
                        partnerTenant={partnerTenant as Tenant}
                    />
                )}

                {activeTab === 'edit' && (
                    <EditTenant
                        tenant={tenant as Tenant}
                        onSuccessFn={refetchData}
                    />
                )}
            </Box>
        </>
    );
};
